import { PageHeader } from "@ant-design/pro-components";
import { Message } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import {
  normalizeVariableList,
  placeholdersMissingFromList,
  renderSmsTemplate,
} from "../../../Util/smsTemplateVariables";
import styles from "./SmsTemplateEdit.module.css";

const SmsTemplateEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const textRef = useRef(null);
  const textWithoutLinkRef = useRef(null);
  const caretRef = useRef({
    text: { start: 0, end: 0 },
    textWithoutLink: { start: 0, end: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [key, setKey] = useState("");
  const [multiline, setMultiline] = useState(false);
  const [text, setText] = useState("");
  const [textWithoutLink, setTextWithoutLink] = useState("");
  const [variables, setVariables] = useState([]);
  const [samples, setSamples] = useState({});
  const [activeField, setActiveField] = useState("text");
  const [result, setResult] = useState({ message: "", type: "" });

  useEffect(() => {
    setLoading(true);
    setLoaded(false);
    api
      .get("/smstemplate/details/" + id)
      .then((res) => {
        const data = res.data?.data;
        if (!data) {
          setResult({ message: "قالب پیدا نشد.", type: "error" });
          return;
        }
        const nextVariables = normalizeVariableList(data.valid_variables);
        setTitle(data.title || "");
        setKey(data.key || "");
        setMultiline(Boolean(data.multiline));
        setText(data.text || "");
        setTextWithoutLink(data.text_without_link || "");
        caretRef.current = {
          text: {
            start: (data.text || "").length,
            end: (data.text || "").length,
          },
          textWithoutLink: {
            start: (data.text_without_link || "").length,
            end: (data.text_without_link || "").length,
          },
        };
        setVariables(nextVariables);
        setSamples(
          nextVariables.reduce((acc, name) => {
            acc[name] = "";
            return acc;
          }, {})
        );
        setLoaded(true);
      })
      .catch((error) => {
        setResult({
          message:
            error.response?.data?.error?.message || "خطا در دریافت قالب",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const missingVariables = placeholdersMissingFromList(
    [text, textWithoutLink],
    variables
  );

  const rememberCaret = (field) => {
    const element = (
      field === "text" ? textRef : textWithoutLinkRef
    ).current?.resizableTextArea?.textArea;
    if (!element) return;
    caretRef.current[field] = {
      start: element.selectionStart ?? 0,
      end: element.selectionEnd ?? 0,
    };
  };

  const insertVariable = (name) => {
    const token = `$${name}$`;
    const field = activeField === "text" ? "text" : "textWithoutLink";
    const value = field === "text" ? text : textWithoutLink;
    const setter = field === "text" ? setText : setTextWithoutLink;
    const element = (
      field === "text" ? textRef : textWithoutLinkRef
    ).current?.resizableTextArea?.textArea;
    const saved = caretRef.current[field];
    const start = Math.min(saved.start ?? value.length, value.length);
    const end = Math.min(saved.end ?? value.length, value.length);
    setter(value.slice(0, start) + token + value.slice(end));
    const cursor = start + token.length;
    caretRef.current[field] = { start: cursor, end: cursor };

    if (!element) return;
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(cursor, cursor);
    });
  };

  const save = () => {
    if (!title.trim()) {
      setResult({ message: "عنوان را وارد کنید.", type: "error" });
      return;
    }
    if (!text.trim()) {
      setResult({ message: "متن پیام را وارد کنید.", type: "error" });
      return;
    }
    if (missingVariables.length) {
      setResult({
        message:
          "این متغیرها در متن هستند ولی برای این قالب تعریف نشده‌اند: " +
          missingVariables.join("، "),
        type: "error",
      });
      return;
    }

    setSaving(true);
    setResult({ message: "", type: "" });
    api
      .post("/smstemplate/edit/" + id, {
        title: title.trim(),
        text,
        text_without_link: textWithoutLink,
        multiline,
      })
      .then((res) => {
        if (res.data?.status === "success") {
          setResult({ message: "ویرایش با موفقیت انجام شد.", type: "success" });
        } else {
          setResult({
            message: "مشکلی در ویرایش پیش آمده است.",
            type: "error",
          });
        }
      })
      .catch((error) => {
        setResult({
          message:
            error.response?.data?.error?.message ||
            "مشکلی در ویرایش پیش آمده است.",
          type: "error",
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <PageHeader
        title={"ویرایش قالب" + (title ? " " + title : "")}
        avatar={{
          icon: <Message />,
          size: "large",
          style: { paddingTop: "8px" },
        }}
      />

      <Card className={styles.form}>
        {loading ? (
          <Spin />
        ) : !loaded ? (
          result.message && <Alert message={result.message} type={result.type} />
        ) : (
          <Form layout="vertical" autoComplete="off">
            <Form.Item label="عنوان">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </Form.Item>

            <Form.Item label="کلید">
              <Input value={key} disabled dir="ltr" />
            </Form.Item>

            <div className={styles.field}>
              <span className={styles.sectionTitle}>متغیرها</span>
              <Typography.Text className={styles.hint}>
                فقط متغیرهای تعریف‌شده را می‌توانید استفاده کنید. کلیک روی هر
                متغیر آن را در{" "}
                {activeField === "text" ? "متن پیام" : "متن بدون لینک"} درج
                می‌کند.
              </Typography.Text>
              {variables.length === 0 ? (
                <Typography.Text className={styles.hint}>
                  این قالب متغیری ندارد.
                </Typography.Text>
              ) : (
                <div className={styles.tags}>
                  {variables.map((name) => (
                    <Tooltip key={name} title="درج در متن فعال">
                      <Tag
                        className={styles.tag}
                        onClick={() => insertVariable(name)}
                      >
                        <span dir="ltr">{`$${name}$`}</span>
                      </Tag>
                    </Tooltip>
                  ))}
                </div>
              )}
              {missingVariables.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    "این متغیرها در متن هستند ولی برای این قالب تعریف نشده‌اند: " +
                    missingVariables.join("، ")
                  }
                />
              )}
              {variables.length > 0 && (
                <div className={styles.samples}>
                  <Typography.Text className={styles.samplesHint}>
                    مقدارهای نمونه فقط برای پیش‌نمایش هستند و ذخیره نمی‌شوند.
                  </Typography.Text>
                  {variables.map((name) => (
                    <FragmentRow
                      key={name}
                      name={name}
                      value={samples[name] || ""}
                      onChange={(value) =>
                        setSamples((current) => ({ ...current, [name]: value }))
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            <div className={styles.field}>
              <label>متن پیام</label>
              <Input.TextArea
                ref={textRef}
                value={text}
                rows={8}
                onFocus={() => setActiveField("text")}
                onClick={() => rememberCaret("text")}
                onKeyUp={() => rememberCaret("text")}
                onSelect={() => rememberCaret("text")}
                onBlur={() => rememberCaret("text")}
                onChange={(e) => setText(e.target.value)}
              />
              <span className={styles.count}>{text.length} کاراکتر</span>
              <div className={styles.preview}>
                {renderSmsTemplate(text, samples) || "پیش‌نمایش متن پیام"}
              </div>
            </div>

            <div className={styles.field}>
              <label>متن بدون لینک</label>
              <Typography.Text className={styles.hint}>
                برای خط‌هایی که ارسال لینک مجاز نیست استفاده می‌شود.
              </Typography.Text>
              <Input.TextArea
                ref={textWithoutLinkRef}
                value={textWithoutLink}
                rows={5}
                onFocus={() => setActiveField("textWithoutLink")}
                onClick={() => rememberCaret("textWithoutLink")}
                onKeyUp={() => rememberCaret("textWithoutLink")}
                onSelect={() => rememberCaret("textWithoutLink")}
                onBlur={() => rememberCaret("textWithoutLink")}
                onChange={(e) => setTextWithoutLink(e.target.value)}
              />
              <span className={styles.count}>
                {textWithoutLink.length} کاراکتر
              </span>
              <div className={styles.preview}>
                {renderSmsTemplate(textWithoutLink, samples) ||
                  "پیش‌نمایش متن بدون لینک"}
              </div>
            </div>

            <div className={styles.actions}>
              <Button type="primary" loading={saving} onClick={save}>
                ذخیره
              </Button>
              <Button onClick={() => navigate("/sms-templates")}>بازگشت</Button>
            </div>

            {result.message && (
              <Alert
                style={{ marginTop: 16 }}
                message={result.message}
                type={result.type}
              />
            )}
          </Form>
        )}
      </Card>
    </div>
  );
};

const FragmentRow = ({ name, value, onChange }) => (
  <>
    <span className={styles.sampleName} dir="ltr">{`$${name}$`}</span>
    <Input
      value={value}
      placeholder="مقدار نمونه برای پیش‌نمایش"
      onChange={(e) => onChange(e.target.value)}
    />
  </>
);

export default memo(SmsTemplateEdit);
